/**
 * useContactManager Hook
 *
 * Sends a message to manager for an order using URQL mutation.
 */

import { useCallback } from "react";
import { useMutation } from "urql";
import { errorTracking } from "@/lib/error-tracking";

// =============================================================================
// MUTATION DOCUMENT
// =============================================================================

const ContactManagerDocument = `
  mutation ContactManager($orderId: ID!, $message: String!) {
    contactManager(orderId: $orderId, message: $message)
  }
`;

// =============================================================================
// HOOK
// =============================================================================

interface UseContactManagerResult {
  contactManager: (orderId: string, message: string) => Promise<boolean>;
  isLoading: boolean;
  error: Error | null;
}

/**
 * Send a message to manager for an order.
 *
 * Uses URQL mutation.
 */
export function useContactManager(): UseContactManagerResult {
  const [result, executeMutation] = useMutation<
    { contactManager: boolean },
    { orderId: string; message: string }
  >(ContactManagerDocument);

  const contactManager = useCallback(
    async (orderId: string, message: string): Promise<boolean> => {
      try {
        const response = await executeMutation({ orderId, message });

        if (response.error) {
          throw new Error(
            response.error.graphQLErrors[0]?.message ||
              response.error.message ||
              "Failed to contact manager"
          );
        }

        const success = response.data?.contactManager ?? false;

        errorTracking.addBreadcrumb({
          category: "order",
          message: "Contacted manager",
          data: { orderId, success },
        });

        return success;
      } catch (err) {
        const contactError =
          err instanceof Error ? err : new Error("Failed to contact manager");

        errorTracking.captureError(contactError, {
          tags: { feature: "orders", action: "contactManager" },
          extra: { orderId },
        });

        throw contactError;
      }
    },
    [executeMutation]
  );

  return {
    contactManager,
    isLoading: result.fetching,
    error: result.error ?? null,
  };
}
